import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

const METHOD_COLORS = { GET: '#4ade80', POST: '#60a5fa', PUT: '#f59e0b', DELETE: '#f87171' };

const ENDPOINTS = (testNumber, phoneId) => [
  {
    group: 'Auth',
    items: [
      {
        method: 'POST', path: '/api/auth/login', auth: false,
        desc: 'Get JWT token. Use this token in all protected endpoints.',
        body: { username: 'admin', password: 'yourpassword' },
        response: { token: 'eyJ...', username: 'admin' },
      },
    ],
  },
  {
    group: 'Messages',
    items: [
      {
        method: 'GET', path: '/api/messages/contacts', auth: true,
        desc: 'Get all contacts with last message and activity time.',
        response: [{ wa_id: testNumber, name: 'Varun', message_count: 5, last_message: 'hi' }],
      },
      {
        method: 'GET', path: '/api/messages/conversation/:waId', auth: true,
        desc: 'Get full conversation with a specific contact.',
        pathParams: [{ name: 'waId', example: testNumber, desc: 'WhatsApp number without +' }],
        response: { messages: [], contact: { wa_id: testNumber, name: 'Varun' } },
      },
      {
        method: 'GET', path: '/api/messages/inbox', auth: true,
        desc: 'Get all messages paginated.',
        queryParams: [{ name: 'page', default: '1' }, { name: 'limit', default: '50' }],
        response: { messages: [], total: 10, page: 1 },
      },
      {
        method: 'POST', path: '/api/messages/send/text', auth: true,
        desc: 'Send a plain text message to any WhatsApp number.',
        body: { to: testNumber, text: 'Hello from WA Dashboard!' },
        response: { success: true, result: { messages: [{ id: 'wamid...' }] } },
      },
      {
        method: 'POST', path: '/api/messages/send/template', auth: true,
        desc: 'Send an approved template message with optional variable parameters.',
        body: {
          to: testNumber,
          templateName: 'hello_world',
          languageCode: 'en_US',
          components: [{ type: 'BODY', parameters: [{ type: 'text', text: 'Varun' }] }],
        },
        response: { success: true, result: { messages: [{ id: 'wamid...' }] } },
      },
    ],
  },
  {
    group: 'Templates',
    items: [
      {
        method: 'GET', path: '/api/templates', auth: true,
        desc: 'Get cached templates from local DB.',
        response: [{ id: 1, name: 'hello_world', status: 'APPROVED', language: 'en_US', category: 'UTILITY' }],
      },
      {
        method: 'GET', path: '/api/templates/sync', auth: true,
        desc: 'Sync all templates from Meta and cache locally. Run this to refresh status.',
        response: { synced: 5, templates: [] },
      },
      {
        method: 'POST', path: '/api/templates/create', auth: true,
        desc: 'Create & submit new template to Meta for approval.',
        body: {
          name: 'order_update',
          category: 'UTILITY',
          language: 'en_US',
          header_type: 'text',
          header_text: 'Order Update',
          body_text: 'Hi {{1}}, your order {{2}} has been shipped!',
          footer_text: 'Reply STOP to unsubscribe',
          body_examples: ['Varun', '#12345'],
          buttons: [{ type: 'QUICK_REPLY', text: 'Track Order' }],
        },
        response: { success: true, template_id: '123456', status: 'PENDING' },
      },
      {
        method: 'DELETE', path: '/api/templates/:name', auth: true,
        desc: 'Delete a template from Meta and local DB.',
        pathParams: [{ name: 'name', example: 'order_update', desc: 'Template name' }],
        response: { success: true },
      },
    ],
  },
  {
    group: 'Bot Flows',
    items: [
      {
        method: 'GET', path: '/api/flows', auth: true,
        desc: 'List all chatbot flows.',
        response: [{ id: 1, name: 'Support Bot', trigger_keyword: 'hi', is_active: 1 }],
      },
      {
        method: 'POST', path: '/api/flows', auth: true,
        desc: 'Create a new chatbot flow.',
        body: { name: 'Support Bot', trigger_keyword: 'hi', flow_json: '{"steps":[{"id":"start","type":"message","text":"Welcome!","next":"end"},{"id":"end","type":"end","text":"Bye!"}]}' },
        response: { id: 1, success: true },
      },
      {
        method: 'PUT', path: '/api/flows/:id', auth: true,
        desc: 'Update a flow (name, trigger, is_active, flow_json).',
        pathParams: [{ name: 'id', example: '1', desc: 'Flow ID' }],
        body: { is_active: 0 },
        response: { success: true },
      },
      {
        method: 'DELETE', path: '/api/flows/:id', auth: true,
        desc: 'Delete a flow permanently.',
        pathParams: [{ name: 'id', example: '1', desc: 'Flow ID' }],
        response: { success: true },
      },
    ],
  },
  {
    group: 'Webhook',
    items: [
      {
        method: 'GET', path: '/api/webhook', auth: false,
        desc: 'Meta webhook verification. Set this URL in Meta Developer Console.',
        queryParams: [
          { name: 'hub.mode', default: 'subscribe' },
          { name: 'hub.verify_token', default: 'your_verify_token' },
          { name: 'hub.challenge', default: '123456' },
        ],
        response: '123456 (challenge echoed back)',
      },
      {
        method: 'POST', path: '/api/webhook', auth: false,
        desc: 'Receive WhatsApp events from Meta or forwarded from n8n.',
        body: { entry: [{ changes: [{ value: { messages: [], contacts: [], statuses: [] } }] }] },
        response: 'OK',
      },
    ],
  },
];

function getTemplateVars(template) {
  if (!template) return { bodyVars: [], bodyText: '', footerText: '', buttons: [], hasHeader: false, headerFormat: null };
  let components = [];
  try { components = JSON.parse(template.components || '[]'); } catch {}
  const body = components.find(c => c.type === 'BODY');
  const header = components.find(c => c.type === 'HEADER');
  const footer = components.find(c => c.type === 'FOOTER');
  const buttonsComp = components.find(c => c.type === 'BUTTONS');
  const extractVars = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  };
  return {
    bodyVars: extractVars(body?.text),
    bodyText: body?.text || '',
    footerText: footer?.text || '',
    buttons: buttonsComp?.buttons || [],
    hasHeader: !!header,
    headerFormat: header?.format || null,
    hasMediaHeader: !!header && !!header.format && header.format !== 'TEXT',
  };
}

export default function ApiDocs() {
  const BASE = window.location.origin;
  const { activeNumber, activeBusiness } = useNumbers();

  const [token, setToken] = useState(localStorage.getItem('wa_token') || '');
  const [apiKey, setApiKey] = useState(localStorage.getItem('wa_api_key') || '');
  const [openItem, setOpenItem] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  const [customBodies, setCustomBodies] = useState({});
  const [copied, setCopied] = useState(null);
  const [config, setConfig] = useState({ testNumber: '919876543210', phoneNumberId: '' });
  const [activeTab, setActiveTab] = useState('docs'); // docs | sandbox

  // Sandbox state
  const [sbMode, setSbMode] = useState('text');
  const [sbTo, setSbTo] = useState('');
  const [sbText, setSbText] = useState('');
  const [sbTemplate, setSbTemplate] = useState('');
  const [sbLang, setSbLang] = useState('en_US');
  const [sbParams, setSbParams] = useState(['']);
  const [sbHeaderUrl, setSbHeaderUrl] = useState('');
  const [sbUploading, setSbUploading] = useState(false);
  const [sbResult, setSbResult] = useState(null);
  const [sbError, setSbError] = useState('');
  const [sbSending, setSbSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const sbFileRef = React.useRef();

  // Auto-resize params when template changes in sandbox
  useEffect(() => {
    if (!sbTemplate) return;
    const tpl = templates.find(t => t.name === sbTemplate && t.language === sbLang);
    const vars = getTemplateVars(tpl);
    if (vars.bodyVars.length > 0) setSbParams(Array(vars.bodyVars.length).fill(''));
    else setSbParams(['']);
    setSbHeaderUrl(''); // reset header URL when template changes
  }, [sbTemplate, sbLang]); // eslint-disable-line

  useEffect(() => {
    // Build config from active number context instead of /api/config
    if (activeNumber) {
      const cfg = {
        phoneNumberId: activeNumber.phone_number_id,
        businessId:    activeBusiness?.business_account_id || '',
        testNumber:    activeNumber.display_phone || '',
        label:         activeNumber.label,
      };
      setConfig(cfg);
    }
    // Load templates for sandbox
    const numId = activeNumber?.id;
    api.get('/templates' + (numId ? '?wa_number_id=' + numId : ''))
      .then(r => setTemplates(r.data)).catch(() => {});
  }, [activeNumber, activeBusiness]);

  const endpoints = ENDPOINTS(config.testNumber || '919876543210', config.phoneNumberId);

  const generateApiKey = () => {
    const key = 'wak_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    setApiKey(key);
    localStorage.setItem('wa_api_key', key);
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const buildCurl = (ep, customBody) => {
    const pathWithExamples = ep.path.replace(/:(\w+)/g, (_, p) => {
      const param = ep.pathParams?.find(pp => pp.name === p);
      return param?.example || p;
    });
    const url = `${BASE}${pathWithExamples}`;
    const body = customBody ? (() => { try { return JSON.parse(customBody); } catch { return ep.body; } })() : ep.body;
    const hasBody = body && ['POST', 'PUT', 'DELETE'].includes(ep.method);
    let cmd = `curl -X ${ep.method} "${url}"`;
    if (ep.auth) cmd += ` \\\n  -H "Authorization: Bearer ${token || 'YOUR_JWT_TOKEN'}"`;
    if (hasBody) {
      cmd += ` \\\n  -H "Content-Type: application/json"`;
      cmd += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    }
    return cmd;
  };

  const runTest = async (ep, key) => {
    setTesting(t => ({ ...t, [key]: true }));
    const customBody = customBodies[key];
    let body;
    try { body = customBody ? JSON.parse(customBody) : ep.body; }
    catch { setTestResults(r => ({ ...r, [key]: { error: 'Invalid JSON in body' } })); setTesting(t => ({ ...t, [key]: false })); return; }

    try {
      const pathResolved = ep.path.replace(/:(\w+)/g, (_, p) => {
        const param = ep.pathParams?.find(pp => pp.name === p);
        return param?.example || p;
      });
      const opts = {
        method: ep.method,
        headers: { 'Content-Type': 'application/json', ...(ep.auth && token ? { Authorization: `Bearer ${token}` } : {}) },
        ...(body && ['POST', 'PUT', 'DELETE'].includes(ep.method) ? { body: JSON.stringify(body) } : {}),
      };
      const res = await fetch(pathResolved, opts);
      const text = await res.text();
      let parsed; try { parsed = JSON.parse(text); } catch { parsed = text; }
      setTestResults(r => ({ ...r, [key]: { status: res.status, data: parsed } }));
    } catch (e) {
      setTestResults(r => ({ ...r, [key]: { error: e.message } }));
    }
    setTesting(t => ({ ...t, [key]: false }));
  };

  // Sandbox file upload
  const handleSbFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSbUploading(true);
    setSbError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/uploads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSbHeaderUrl(res.data.url);
    } catch (err) {
      setSbError('File upload failed: ' + (err.response?.data?.error || err.message));
    }
    setSbUploading(false);
  };

  // Sandbox send
  const sandboxSend = async () => {
    setSbSending(true); setSbResult(null); setSbError('');
    try {
      let res;
      if (sbMode === 'text') {
        res = await api.post('/messages/send/text', { to: sbTo, text: sbText });
      } else {
        const tpl = templates.find(t => t.name === sbTemplate && t.language === sbLang);
        const vars = getTemplateVars(tpl);
        const components = [];

        // Header — media types only (DOCUMENT, IMAGE, VIDEO)
        if (vars.hasHeader && vars.headerFormat && vars.headerFormat !== 'TEXT' && sbHeaderUrl.trim()) {
          const mediaType = vars.headerFormat.toLowerCase();
          components.push({
            type: 'header',
            parameters: [{ type: mediaType, [mediaType]: { link: sbHeaderUrl.trim() } }],
          });
        }

        // Body params
        const filled = sbParams.filter(p => p.trim());
        if (filled.length > 0) {
          components.push({
            type: 'body',
            parameters: vars.bodyVars.length > 0
              ? vars.bodyVars.map((varName, i) => {
                  const param = { type: 'text', text: sbParams[i] || '' };
                  if (isNaN(varName)) param.parameter_name = varName;
                  return param;
                })
              : filled.map(p => ({ type: 'text', text: p })),
          });
        }

        res = await api.post('/messages/send/template', { to: sbTo, templateName: sbTemplate, languageCode: sbLang, components });
      }
      setSbResult(res.data);
    } catch (e) { setSbError(e.response?.data?.error || 'Failed'); }
    setSbSending(false);
  };

  const buildSbCurl = () => {
    const authHeader = `-H "Authorization: Bearer ${token || 'YOUR_TOKEN'}"`;
    const ctHeader = `-H "Content-Type: application/json"`;
    if (sbMode === 'text') {
      const body = JSON.stringify({ to: sbTo, text: sbText }, null, 2);
      return `curl -X POST "${BASE}/api/messages/send/text" \\\n  ${authHeader} \\\n  ${ctHeader} \\\n  -d '${body}'`;
    } else {
      const tpl = templates.find(t => t.name === sbTemplate && t.language === sbLang);
      const vars = getTemplateVars(tpl);
      const comps = [];

      // Header component for media templates
      if (vars.hasMediaHeader && sbHeaderUrl.trim()) {
        const mediaType = vars.headerFormat.toLowerCase();
        comps.push({ type: 'header', parameters: [{ type: mediaType, [mediaType]: { link: sbHeaderUrl.trim() } }] });
      }

      // Body component
      const filled = sbParams.filter(p => p);
      if (filled.length > 0) {
        comps.push({
          type: 'body',
          parameters: vars.bodyVars.length > 0
            ? vars.bodyVars.map((varName, i) => {
                const param = { type: 'text', text: sbParams[i] || '' };
                if (isNaN(varName)) param.parameter_name = varName;
                return param;
              })
            : filled.map(p => ({ type: 'text', text: p })),
        });
      }

      const body = JSON.stringify({ to: sbTo, templateName: sbTemplate, languageCode: sbLang, components: comps }, null, 2);
      return `curl -X POST "${BASE}/api/messages/send/template" \\\n  ${authHeader} \\\n  ${ctHeader} \\\n  -d '${body}'`;
    }
  };
  const sbCurl = buildSbCurl();

  return (
    <div style={{ padding: '20px 16px', maxWidth: 820, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 4 }}>
          API DOCS & SANDBOX
        </h2>
        <p style={{ color: 'var(--text3)', fontSize: 12 }}>
          Base: <code style={{ color: 'var(--accent2)', background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4, fontFamily: 'var(--mono)' }}>{BASE}</code>
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--r)', padding: 4, marginBottom: 20, border: '1px solid var(--border)', width: 'fit-content', gap: 4 }}>
        {[['docs', '📖 Docs'], ['sandbox', '🧪 Sandbox']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: '7px 18px', background: activeTab === id ? 'var(--accent)' : 'transparent',
            color: activeTab === id ? '#000' : 'var(--text2)',
            fontWeight: activeTab === id ? 700 : 400,
            borderRadius: 6, border: 'none', fontSize: 13,
          }}>{label}</button>
        ))}
      </div>

      {/* Token + API Key card */}
      <div className="card" style={{ marginBottom: 20, padding: '16px' }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* JWT Token */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="label">JWT Token (for API calls)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={token} onChange={e => { setToken(e.target.value); localStorage.setItem('wa_token', e.target.value); }}
                placeholder="Paste JWT token" style={{ fontFamily: 'var(--mono)', fontSize: 11, flex: 1 }} />
              <button className="btn-secondary" onClick={() => setToken(localStorage.getItem('wa_token') || '')} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                From Login
              </button>
            </div>
          </div>

          {/* API Key generator */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="label">Static API Key (optional, for integrations)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={apiKey} readOnly placeholder="Click Generate to create"
                style={{ fontFamily: 'var(--mono)', fontSize: 10, flex: 1, color: 'var(--accent2)' }} />
              <button className="btn-secondary" onClick={generateApiKey} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                🔑 Generate
              </button>
              {apiKey && (
                <button className="btn-ghost" onClick={() => copyText(apiKey, 'apikey')} style={{ fontSize: 11, padding: '4px 8px', color: copied === 'apikey' ? 'var(--accent)' : 'var(--text3)' }}>
                  {copied === 'apikey' ? '✓' : '📋'}
                </button>
              )}
            </div>
            {apiKey && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>Saved locally. Use as Bearer token. Reset anytime by generating new.</div>}
          </div>
        </div>
      </div>

      {/* SANDBOX TAB */}
      {activeTab === 'sandbox' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', marginBottom: 16 }}>// MESSAGE SANDBOX — Send real messages & see cURL</div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--r-sm)', padding: 3, marginBottom: 16, width: 'fit-content', gap: 3 }}>
              {['text', 'template'].map(m => (
                <button key={m} onClick={() => setSbMode(m)} style={{
                  padding: '6px 16px', background: sbMode === m ? 'var(--accent)' : 'transparent',
                  color: sbMode === m ? '#000' : 'var(--text2)', fontWeight: sbMode === m ? 700 : 400,
                  borderRadius: 4, border: 'none', fontSize: 12,
                }}>{m === 'text' ? '💬 Text' : '📋 Template'}</button>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* To */}
              <div>
                <label className="label">To (WhatsApp number)</label>
                <input value={sbTo} onChange={e => setSbTo(e.target.value)}
                  placeholder="919876543210" style={{ fontFamily: 'var(--mono)' }} inputMode="numeric" />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                  Default from .env: <code style={{ fontFamily: 'var(--mono)', color: 'var(--accent2)' }}>{config.testNumber}</code>
                </div>
              </div>

              {sbMode === 'text' ? (
                <div>
                  <label className="label">Message</label>
                  <textarea value={sbText} onChange={e => setSbText(e.target.value)} rows={3} placeholder="Type your message..." style={{ resize: 'vertical' }} />
                </div>
              ) : (
                <>
                  <div>
                    <label className="label">Template</label>
                    <select value={`${sbTemplate}|${sbLang}`} onChange={e => {
                      const [tName, tLang] = e.target.value.split('|');
                      setSbTemplate(tName);
                      if (tLang) setSbLang(tLang);
                    }}>
                      <option value="|">— Select template —</option>
                      {templates.filter(t => t.status === 'APPROVED').map(t => (
                        <option key={t.id} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Language Code</label>
                    <input value={sbLang} onChange={e => setSbLang(e.target.value)} style={{ maxWidth: 120 }} />
                  </div>
                  {(() => {
                    const tpl = templates.find(t => t.name === sbTemplate && t.language === sbLang);
                    const vars = getTemplateVars(tpl);
                    const mediaLabel = vars.headerFormat === 'DOCUMENT' ? 'PDF Document'
                      : vars.headerFormat === 'IMAGE' ? 'Image'
                      : vars.headerFormat === 'VIDEO' ? 'Video' : '';
                    const mediaAccept = vars.headerFormat === 'DOCUMENT' ? '.pdf'
                      : vars.headerFormat === 'IMAGE' ? '.jpg,.jpeg,.png'
                      : vars.headerFormat === 'VIDEO' ? '.mp4' : '*';
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* Media header upload — only for DOCUMENT/IMAGE/VIDEO */}
                        {vars.hasMediaHeader && (
                          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                              {vars.headerFormat === 'DOCUMENT' ? '📄' : vars.headerFormat === 'IMAGE' ? '🖼️' : '🎥'} Header {mediaLabel}
                              <span style={{ color: 'var(--danger)', marginLeft: 4 }}>required</span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                              <button
                                className="btn-secondary"
                                onClick={() => sbFileRef.current?.click()}
                                disabled={sbUploading}
                                style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
                              >
                                {sbUploading ? '⏳ Uploading...' : `📁 Upload ${mediaLabel}`}
                              </button>
                              <span style={{ fontSize: 11, color: 'var(--text3)' }}>or paste URL below</span>
                            </div>
                            <input
                              ref={sbFileRef}
                              type="file"
                              accept={mediaAccept}
                              style={{ display: 'none' }}
                              onChange={handleSbFileUpload}
                            />
                            <input
                              value={sbHeaderUrl}
                              onChange={e => setSbHeaderUrl(e.target.value)}
                              placeholder={`https://yourdomain.com/file.pdf`}
                            />
                            {sbHeaderUrl && (
                              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--accent)', wordBreak: 'break-all' }}>
                                ✅ {sbHeaderUrl}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Body vars */}
                        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '12px 14px' }}>
                          {vars.bodyText && (
                            <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap', lineHeight: 1.5, borderLeft: '3px solid var(--border2)', paddingLeft: 10 }}>
                              {vars.bodyText}
                            </div>
                          )}
                          {vars.bodyVars.length > 0 ? (
                            <>
                              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                {vars.bodyVars.length} Variable{vars.bodyVars.length > 1 ? 's' : ''} detected
                              </div>
                              {vars.bodyVars.map((varName, i) => (
                                <div key={varName} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                  <span style={{ color: 'var(--accent2)', fontFamily: 'var(--mono)', fontSize: 11, paddingTop: 10, minWidth: 60, flexShrink: 0 }}>
                                    {"{{" + varName + "}}"}
                                  </span>
                                  <input value={sbParams[i] || ''} onChange={e => { const u=[...sbParams]; u[i]=e.target.value; setSbParams(u); }} placeholder={"Value for " + varName} />
                                </div>
                              ))}
                            </>
                          ) : (
                            <div style={{ fontSize: 12, color: 'var(--accent)' }}>✅ No variables — sends as-is</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              {/* cURL preview */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className="label" style={{ margin: 0 }}>Generated cURL</label>
                  <button className="btn-ghost" onClick={() => copyText(sbCurl, 'sbcurl')} style={{ fontSize: 11, color: copied === 'sbcurl' ? 'var(--accent)' : 'var(--text3)' }}>
                    {copied === 'sbcurl' ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
                <pre style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', padding: '10px 14px',
                  fontSize: 11, fontFamily: 'var(--mono)', color: '#a8ff78',
                  overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.7,
                }}>
                  {sbCurl}
                </pre>
              </div>

              {sbError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 13 }}>⚠ {sbError}</div>
              )}
              {sbResult && (
                <div style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid var(--accent)', borderRadius: 'var(--r-sm)', padding: '10px 14px' }}>
                  <div style={{ color: 'var(--accent)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>✅ Sent!</div>
                  <pre style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)', margin: 0 }}>{JSON.stringify(sbResult, null, 2)}</pre>
                </div>
              )}

              <button className="btn-primary" onClick={sandboxSend}
                disabled={sbSending || !sbTo || (sbMode==='text' && !sbText) || (sbMode==='template' && !sbTemplate)}
                style={{ padding: '12px', fontSize: 14 }}>
                {sbSending ? '⏳ Sending…' : '🚀 Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOCS TAB */}
      {activeTab === 'docs' && endpoints.map(group => (
        <div key={group.group} style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent2)', letterSpacing: '0.08em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
            // {group.group.toUpperCase()}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.items.map((ep, idx) => {
              const key = `${group.group}-${idx}`;
              const isOpen = openItem === key;
              const result = testResults[key];
              const customBody = customBodies[key];

              return (
                <div key={key} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div onClick={() => setOpenItem(isOpen ? null : key)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: METHOD_COLORS[ep.method], minWidth: 52 }}>{ep.method}</span>
                    <code style={{ fontSize: 12, color: 'var(--text)', fontFamily: 'var(--mono)', flex: 1 }}>{ep.path}</code>
                    <span className={`tag ${ep.auth ? 'tag-gray' : 'tag-green'}`} style={{ fontSize: 10 }}>{ep.auth ? '🔒 Auth' : 'Public'}</span>
                    <span style={{ color: 'var(--text3)', fontSize: 13 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>

                  {isOpen && (
                    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg3)', padding: 16 }}>
                      <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>{ep.desc}</p>

                      {ep.pathParams && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Path Params</div>
                          {ep.pathParams.map(p => (
                            <div key={p.name} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4, display: 'flex', gap: 8 }}>
                              <code style={{ color: 'var(--accent2)', fontFamily: 'var(--mono)' }}>:{p.name}</code>
                              <span style={{ color: 'var(--text3)' }}>{p.desc} — e.g.</span>
                              <code style={{ fontFamily: 'var(--mono)', color: 'var(--text)' }}>{p.example}</code>
                            </div>
                          ))}
                        </div>
                      )}

                      {ep.queryParams && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Query Params</div>
                          {ep.queryParams.map(p => (
                            <div key={p.name} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4, display: 'flex', gap: 8 }}>
                              <code style={{ color: 'var(--accent2)', fontFamily: 'var(--mono)' }}>{p.name}</code>
                              <span style={{ color: 'var(--text3)' }}>default: <code style={{ fontFamily: 'var(--mono)' }}>{p.default}</code></span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* cURL */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>cURL</div>
                          <button className="btn-ghost" onClick={() => copyText(buildCurl(ep, customBody), key+'-curl')}
                            style={{ fontSize: 11, padding: '3px 8px', color: copied===key+'-curl' ? 'var(--accent)' : 'var(--text3)' }}>
                            {copied===key+'-curl' ? '✓ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                        <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 11, fontFamily: 'var(--mono)', color: '#a8ff78', overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.7 }}>
                          {buildCurl(ep, customBody)}
                        </pre>
                      </div>

                      {/* Body editor */}
                      {ep.body && ['POST', 'PUT', 'DELETE'].includes(ep.method) && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Request Body (editable)</div>
                          <textarea
                            value={customBody ?? JSON.stringify(ep.body, null, 2)}
                            onChange={e => setCustomBodies(b => ({ ...b, [key]: e.target.value }))}
                            rows={Math.min(Object.keys(ep.body).length + 4, 14)}
                            style={{ fontFamily: 'var(--mono)', fontSize: 11, resize: 'vertical', lineHeight: 1.6 }}
                          />
                        </div>
                      )}

                      {/* Expected response */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Expected Response</div>
                        <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)', overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
                          {typeof ep.response === 'string' ? ep.response : JSON.stringify(ep.response, null, 2)}
                        </pre>
                      </div>

                      {/* Run button */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button className="btn-primary" onClick={() => runTest(ep, key)} disabled={testing[key]} style={{ fontSize: 13 }}>
                          {testing[key] ? '⏳ Running…' : '▶ Run in Browser'}
                        </button>
                        {result && (
                          <button className="btn-ghost" onClick={() => copyText(JSON.stringify(result, null, 2), key+'-res')}
                            style={{ fontSize: 12, color: copied===key+'-res' ? 'var(--accent)' : 'var(--text3)' }}>
                            {copied===key+'-res' ? '✓ Copied' : '📋 Copy result'}
                          </button>
                        )}
                      </div>

                      {result && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: result.error ? 'var(--danger)' : 'var(--accent)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {result.error ? '✗ Error' : `✓ ${result.status}`}
                          </div>
                          <pre style={{ background: 'var(--bg)', border: `1px solid ${result.error ? 'var(--danger)' : 'var(--accent)'}`, borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 11, fontFamily: 'var(--mono)', color: result.error ? 'var(--danger)' : 'var(--text)', overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0, maxHeight: 300, overflowY: 'auto' }}>
                            {result.error || JSON.stringify(result.data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}